import React from "react";

import { useTranslations } from "next-intl";

import { Card } from "@/components/ui";
import IconByName from "@/components/ui/IconByName";
import IconShower from "@/icons/content/property/shower-head.svg";
import { ShowMore } from "@/ui/Common/ShowMore";

interface ObjectDetailAmenitiesProps
  extends React.HTMLAttributes<HTMLDivElement> {
  options?: Record<string, any>;
}

/**
 * Map special amenity names to their specific custom SVG icons.
 */
const CUSTOM_ICON_MAP: Record<
  string,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  showerCabin: IconShower,
};

export const ObjectDetailAmenities = ({
  options,
  className,
}: ObjectDetailAmenitiesProps) => {
  const tAdditional = useTranslations("AdditionalOptions");
  const t = useTranslations();

  /**
   * Helper function to decide which icon to render.
   */
  const renderAmenityIcon = (item: any) => {
    if (!item) return null;

    // If the item has a special custom icon
    const CustomIcon = CUSTOM_ICON_MAP[item.name];
    if (CustomIcon) {
      return <CustomIcon className="w-[2.8rem] h-auto flex-shrink-0" />;
    }

    // Otherwise, use the dynamic IconByName if a valid string is provided
    if (typeof item.icon === "string") {
      return (
        <IconByName
          name={item.icon}
          stroke={1}
          className="w-[2.8rem] h-auto flex-shrink-0"
        />
      );
    }

    return null;
  };

  // Early return if there are no options
  if (!options || Object.keys(options).length === 0) {
    return (
      <section className={className}>
        <h2 className="object-detail__section-header mb-[3rem]">
          {t("Additional options")}
        </h2>
        <Card className="p-12 text-center text-[#777]">
          {t("No additional options available")}
        </Card>
      </section>
    );
  }

  return (
    <section className={className}>
      <h2 className="object-detail__section-header mb-[3rem]">
        {t("Additional options")}
      </h2>
      <Card className="p-12">
        <ShowMore
          maxHeight={190}
          titleCollapsed={t("Learn more")}
          titleExpanded={t("Collapse")}
        >
          <div className="grid grid-cols-1 gap-[2rem]">
            {Object.entries(options).map(([groupName, items]) => {
              if (!Array.isArray(items) || items.length === 0) return null;

              return (
                <div
                  key={groupName}
                  className="grid grid-cols-1 gap-[1rem]"
                >
                  <h3 className="list-dot text-[2.1rem] font-medium relative mb-[2rem]">
                    {tAdditional(groupName)}
                  </h3>

                  <ul className="px-[2rem] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-[2rem]">
                    {items.map((item: any, index: number) => (
                      <li
                        key={index}
                        className="flex items-center gap-[1rem] text-[#323232]"
                      >
                        {renderAmenityIcon(item)}

                        <span className="text-[1.6rem] font-medium text-[#444]">
                          {tAdditional(item.name)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </ShowMore>
      </Card>
    </section>
  );
};
